import type {
  EquipmentCategory,
  EquipmentItem,
  EquipmentItemInput,
  WorkingState,
} from '@mads/shared'

/**
 * The form's own state shape. Every field is a string because that is what
 * inputs hold; `toInput` converts back to the API's types on submit.
 *
 * `lamp` and `watts` hold the item's *override* rather than what it reads
 * (issue #59): empty means "inherit from the fixture model", which is why the
 * form shows the inherited value as a placeholder rather than seeding the box
 * with it. Seeding it would make leaving the field alone indistinguishable
 * from deliberately typing the model's value in, and the way back to inherited
 * is precisely clearing the box.
 */
export interface EquipmentFormValues {
  category: EquipmentCategory
  name: string
  brand: string
  notes: string
  quantity: string
  locationId: string
  madsCode: string
  patTestCode: string
  patTestDate: string
  workingState: WorkingState
  fixtureModelId: string
  lightType: string
  lamp: string
  watts: string
  gelFrame: string
  goboFrame: string
  barnDoor: string
}

export const BLANK_VALUES: EquipmentFormValues = {
  category: 'Lighting Fixture',
  name: '',
  brand: '',
  notes: '',
  quantity: '1',
  locationId: '',
  madsCode: '',
  patTestCode: '',
  patTestDate: '',
  // What a piece of kit nobody has checked actually is (issue #59). Combined
  // with the dashboard flagging it, that makes the attention list a worklist:
  // record the kit, then work through what has not been checked.
  workingState: 'Not tested',
  fixtureModelId: '',
  lightType: '',
  lamp: '',
  watts: '',
  gelFrame: '',
  goboFrame: '',
  barnDoor: '',
}

const ELECTRICAL: EquipmentCategory[] = [
  'Lighting Fixture',
  'Electronic Equipment',
  'Audio Equipment',
]

/** Electrical categories carry PAT status; cabling is stock-counted only. */
export function isElectrical(category: EquipmentCategory): boolean {
  return ELECTRICAL.includes(category)
}

export function valuesFromItem(item: EquipmentItem): EquipmentFormValues {
  const linked = item.fixtureModel !== null

  return {
    category: item.category,
    name: item.name,
    brand: item.brand ?? '',
    notes: item.notes ?? '',
    quantity: String(item.quantity),
    locationId: item.location === null ? '' : String(item.location.id),
    madsCode: item.madsCode ?? '',
    patTestCode: item.patTestCode ?? '',
    patTestDate: item.patTestDate ?? '',
    // `Not applicable` is the state cabling is in, and the form never offers
    // it — reclassifying is what sets it, server-side.
    workingState: item.workingState === 'Not applicable' ? 'Not tested' : item.workingState,
    fixtureModelId: linked ? String(item.fixtureModel?.id) : '',
    // The API already resolves the optics through the fixture model, so an
    // unlinked item seeds the boxes with what it reads (issue #30). A linked
    // one seeds the four shared fields the same way — the form hides them
    // while it stays linked, and unlinking there leaves the same values the
    // API would have copied down anyway.
    lightType: item.lightType ?? '',
    // …but lamp and watts hold the override, so a linked lantern that inherits
    // them starts empty and shows the model's value as a placeholder
    // (issue #59).
    lamp: linked && !item.lampOverridden ? '' : (item.lamp ?? ''),
    watts: linked && !item.wattsOverridden ? '' : item.watts === null ? '' : String(item.watts),
    gelFrame: item.gelFrame ?? '',
    goboFrame: item.goboFrame ?? '',
    barnDoor: item.barnDoor ?? '',
  }
}

const blank = (value: string): string | null => (value.trim() === '' ? null : value.trim())

/**
 * Only sends the fields the chosen category actually uses, so the API never
 * receives a lamp for a coil of cable.
 */
export function toInput(values: EquipmentFormValues): EquipmentItemInput {
  const input: EquipmentItemInput = {
    category: values.category,
    name: values.name.trim(),
    brand: blank(values.brand),
    notes: blank(values.notes),
    quantity: values.quantity === '' ? 0 : Number(values.quantity),
    locationId: values.locationId === '' ? null : Number(values.locationId),
  }

  if (isElectrical(values.category)) {
    input.madsCode = blank(values.madsCode)
    input.patTestCode = blank(values.patTestCode)
    input.patTestDate = values.patTestDate === '' ? null : values.patTestDate
    input.workingState = values.workingState
  }

  if (values.category === 'Lighting Fixture') {
    input.fixtureModelId = values.fixtureModelId === '' ? null : Number(values.fixtureModelId)

    // A linked lantern's shared optics belong to its model, so they are not
    // sent at all — the API would ignore them, and sending them would suggest
    // this form is where they're changed (issue #30).
    if (input.fixtureModelId === null) {
      input.lightType = blank(values.lightType)
      input.gelFrame = blank(values.gelFrame)
      input.goboFrame = blank(values.goboFrame)
      input.barnDoor = blank(values.barnDoor)
    }

    // The lamp and the watts go either way: on a linked lantern they are the
    // override, and an emptied box is what puts it back to inheriting
    // (issue #59).
    input.lamp = blank(values.lamp)
    input.watts = values.watts.trim() === '' ? null : Number(values.watts)
  }

  return input
}
